import { supabase } from '../../Admin/utils/supabaseClient'; // adjust path as needed

// Add a new portfolio project for a user
export async function addPortfolioProject(project, userId) {
  const { title, description, imageUrl, siteUrl, tags, category, status, completionPercentage } = project;

  const { data, error } = await supabase
    .from('ritesh_project_portfolio')
    .insert([
      {
        title,
        description,
        image_url: imageUrl,
        site_url: siteUrl,
        tags,
        category,
        status: status || 'completed',
        completion_percentage: completionPercentage ?? 100,
        user_id: userId,
      },
    ])
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
}

// Update an existing project owned by user
export async function updatePortfolioProject(projectId, updates) {
  const { title, description, imageUrl, siteUrl, tags, category, status, completionPercentage } = updates;

  const { data, error } = await supabase
    .from('ritesh_project_portfolio')
    .update({
      title,
      description,
      image_url: imageUrl,
      site_url: siteUrl,
      tags,
      category,
      status: status || 'completed',
      completion_percentage: completionPercentage ?? 100,
    })
    .eq('id', projectId)
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
}



// Helper to optimize Cloudinary URLs with f_auto,q_auto
export function optimizeCloudinaryUrl(url) {
  if (!url || typeof url !== 'string' || !url.includes('cloudinary.com')) return url;
  // If it already has transformations, add after the first one, or just before 'upload/'
  if (url.includes('f_auto,q_auto')) return url;
  return url.replace('/upload/', '/upload/f_auto,q_auto/');
}

// Get all projects for a specific user
export async function getUserProjects(userId) {
  const { data, error } = await supabase
    .from('ritesh_project_portfolio')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message);
  }

  return (data || []).map(p => ({ ...p, image_url: optimizeCloudinaryUrl(p.image_url) }));
}

// Get all portfolio projects (public or admin view, no user filter)
export async function fetchPortfolioProjects() {
  const { data, error } = await supabase
    .from('ritesh_project_portfolio')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message);
  }

  return (data || []).map(p => ({ ...p, image_url: optimizeCloudinaryUrl(p.image_url) }));
}

// Delete a project owned by the user
export async function deletePortfolioProject(projectId) {
  const { data, error } = await supabase
    .from('ritesh_project_portfolio')
    .delete()
    .eq('id', projectId)
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
}

// Insert multiple sample projects for a user (bulk add)
export async function addSamplePortfolioProjects(sampleProjects, userId) {
  const formattedProjects = sampleProjects.map((project) => ({
    ...project,
    user_id: userId,
  }));

  const { data, error } = await supabase
    .from('ritesh_project_portfolio')
    .insert(formattedProjects)
    .select();

  if (error) {
    throw new Error(error.message);
  }

  return data;
}

// Get all site settings
export async function getSiteSettings() {
  try {
    const { data, error } = await supabase
      .from('ritesh_site_settings')
      .select('*');

    if (error) {
      if (error.code === 'PGRST116' || error.status === 404) {
        console.warn("Settings table 'ritesh_site_settings' not found. Please create it in Supabase.");
        return [];
      }
      throw error;
    }
    return data || [];
  } catch (err) {
    console.error("Error fetching settings:", err.message);
    return [];
  }
}

// Update or Insert Resume URL
export async function updateResumeUrl(url) {
  const { data, error } = await supabase
    .from('ritesh_site_settings')
    .upsert({ key: 'resume_url', value: url }, { onConflict: 'key' })
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
}
